import type {
  DialogueAction,
  DialogueId,
  EncounterId,
  DialogueSpeaker,
  DialogueStepDocument,
} from "../content/types";
import { EventStepAction } from "./actions";

export type DialogueResolver = (
  id: DialogueId,
) => readonly DialogueStepDocument[] | undefined;

export interface EventState {
  txt: string;
  speaker: number;
  readyTime: number;
  counter: number;
  action: EventStepAction;
  encounter?: EncounterId;
  ready: boolean;
  hasTextBox: boolean;
  pose: number;
}

const EVENT_DEFAULTS = {
  txt: "",
  speaker: 0,
  readyTime: 12,
  action: EventStepAction.None,
  hasTextBox: true,
  pose: 0,
} as const;

const SPEAKER_IDS: Readonly<Record<DialogueSpeaker, number>> = {
  asterus: 0,
  lumi: 1,
  crenson: 2,
  "xenos-core": 3,
  unknown: 4,
};

const ACTION_IDS: Readonly<Record<DialogueAction, EventStepAction>> = {
  none: EventStepAction.None,
  "no-text": EventStepAction.NoText,
  "enable-enemy-attacks": EventStepAction.EnableEnemyAttacks,
  "disable-enemy-attacks": EventStepAction.DisableEnemyAttacks,
  "whiteout-on": EventStepAction.WhiteoutOn,
  "whiteout-off": EventStepAction.WhiteoutOff,
  win: EventStepAction.Win,
  "boss-vanish": EventStepAction.BossVanish,
  "boss-stop": EventStepAction.BossStop,
  "boss-resume": EventStepAction.BossResume,
  "activate-encounter": EventStepAction.ActivateEncounter,
  "camera-shake": EventStepAction.CameraShake,
  "show-energy-crusher-unlock": EventStepAction.ShowEnergyCrusherUnlock,
  "show-gravity-slicer-unlock": EventStepAction.ShowGravitySlicerUnlock,
  "show-megatron-unlock": EventStepAction.ShowMegatronUnlock,
  "show-barrier-wing-unlock": EventStepAction.ShowBarrierWingUnlock,
  "start-cleanser": EventStepAction.StartCleanser,
  "final-shard-burst": EventStepAction.FinalShardBurst,
  "boss-flash-and-resume": EventStepAction.BossFlashAndResume,
  "start-boss-fight": EventStepAction.StartBossFight,
  "return-to-main-menu": EventStepAction.ReturnToMainMenu,
};

export interface EventContext {
  generators: number;
  score: number;
}

export function resolveEvent(
  resolveDialogue: DialogueResolver,
  eventId: DialogueId,
  part: number,
  ctx: EventContext,
): EventState {
  const step = resolveDialogue(eventId)?.[part];
  const txt = step?.text ?? EVENT_DEFAULTS.txt;
  return {
    txt: txt.includes("{")
      ? txt
          .replace("{generators}", String(ctx.generators))
          .replace("{score}", String(ctx.score))
      : txt,
    speaker:
      step?.speaker === undefined
        ? EVENT_DEFAULTS.speaker
        : SPEAKER_IDS[step.speaker],
    readyTime: step?.readyFrames ?? EVENT_DEFAULTS.readyTime,
    counter: 0,
    action:
      step?.action === undefined
        ? EVENT_DEFAULTS.action
        : ACTION_IDS[step.action],
    encounter: step?.encounter,
    ready: false,
    hasTextBox: step?.textBox ?? EVENT_DEFAULTS.hasTextBox,
    pose: step?.pose === "alternate" ? 1 : EVENT_DEFAULTS.pose,
  };
}
